import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import {Stack} from 'office-ui-fabric-react/lib/Stack';
import * as React from 'react';
// tslint:disable-next-line:no-duplicate-imports
import { useState } from 'react';
import { match } from 'react-router';
import { displayUtcDate } from '../../common/date';
import {decode} from '../../common/html';
import { Address, canTrackShipment, estimatedDelivery, isDelivered, isShipped,  OrderDetail, OrderProductVariant, pendingReview, shippedOn } from '../../store';
import { getTrackingUrl } from '../../store/shipment';
import { OrderActions, OrderSummary, OrderSummaryInfo, renderOrderHeader } from '../orderList/orderList';
import classNames from './order.classNames';

export interface OrderAttributes {
    order: OrderDetail;
}

export interface OrderActions {
    fetch(id: number, done: () => void);
}

export type OrderProps = OrderAttributes & OrderActions & {
    match: match<{orderId: string}>;
};

enum OrderState {
    loading,
    loaded
}

const renderAddress = (x: Address) => (
    <div>
        {x.firstName} {x.lastName}<br/>
        {x.address1}<br/>
        {x.address2 ? [x.address2, <br/>] : null}
        {x.city}, {x.stateProvinceName} {x.zipPostalCode}<br/>
        {x.countryId === 1 ? null : x.countryName}
    </div>
);

const renderQuantity = (x: number) => x > 1 ? <div>Qty: <span>{x}</span></div> : null;

const renderItemSubTotal = (item: OrderProductVariant) => <label>{item.subTotal} {item.quantity > 1 ?  <span>({item.quantity} x {item.unitPrice})</span> : null}</label>;

const removeLine = (html: string, toRemove: string) => {
    const lines = html.split(/<br\s*\/>/);

    return lines.map(decode)
        .filter(x => !x.startsWith(toRemove))
        // remove all trailing blank lines
        .reverse().reduce((b, line) => !line && b.length === 0 ? b : [...b, line], []).reverse();
};

const renderLine = (id: number) => (line: string, index: number) => [
    line,
    <br/>
];

const renderAttribute = (item: OrderProductVariant) => (
<p>
    {removeLine(item.attributeInfo, 'Expected delivery').map(renderLine(item.id))}
</p>);

const renderItemImage = (item: OrderProductVariant) => (item.itemImageUrl ? <img className={classNames().orderItemImage} src={item.itemImageUrl} alt={item.productName}/> : null);

const renderAddressBlock = (label: string) => (address: Address) => (
    <div className={classNames().addressBlock}>
        <label>{label}</label>
        {renderAddress(address)}
    </div>
);

function renderItem(item: OrderProductVariant, index: number) : JSX.Element {
    return (
        <div key={`item_${index}`} className={classNames().orderItem}>
            {renderItemImage(item)}
            <div className={classNames().orderItemInfo}>
                <label>{item.productName}</label>
                {renderAttribute(item)}
                {renderQuantity(item.quantity)}
                {renderItemSubTotal(item)}
            </div>
        </div>
    );
}

function renderItems(items: any[]) : JSX.Element {
    return (
        <Stack className={classNames().orderItems}>
            {items.map(renderItem)}
        </Stack>
    );
}

const renderOrderCostDetails = (order: OrderDetail) => {
    const {orderSubTotalDiscount, orderTotalDiscount, redeemedRewardPointsAmount} = order;
    const discount = orderSubTotalDiscount || orderTotalDiscount;

    return [
        <div key='order-subTotal'><label>Subtotal:</label><span>{order.orderSubtotal}</span></div>,
        (discount ? <div key='order-discount'><label>Discount:</label><span>{discount}</span></div> : null),
        (redeemedRewardPointsAmount ? <div key='order-points'><label>Store credits:</label><span>{redeemedRewardPointsAmount}</span></div> : null),
        <div key='order-tax'><label>Tax:</label><span>{order.tax}</span></div>
    ];
};

const renderOrderNotes = (order: OrderDetail) => {
    const {orderNotes: notes} = order;
    if (!notes || notes.length === 0) {
        return null;
    }

    return [<br/>, ...notes.map((note, i) => <div className={classNames().note} key={`ordernote-${i}`}><label>{displayUtcDate(note.createdOn)}:</label><span>{note.note}</span></div>)];
};

const renderDelivery = (order: OrderDetail) => [
    isShipped(order) ? <div key='shipped-date'><label>Shipped</label><span>{shippedOn(order)}</span></div> : null,
    <div key='estimated-delivery'><label>{isDelivered(order) ? 'Delivered:' : 'Est. Delivery:'}</label><span>{estimatedDelivery(order)}</span></div>
];

const renderTracking = (order: OrderDetail) => canTrackShipment(order) ? <DefaultButton text='Track My Order' href={getTrackingUrl(order)} target='_blank'/> : null;

const renderReviewLink = (order: OrderDetail) => pendingReview(order) ? <DefaultButton text='Write a review' href={`${order.pendingReviewUrl}`} /> : null;

export const OrderView = (props: OrderProps) => {
    const [state, setState] = useState<OrderState>(OrderState.loading);
    const { order, fetch, match: {params: {orderId} }} = props;

    React.useEffect(() => {
        if (state === OrderState.loading) {
            fetch(parseInt(orderId, 10), () => {
                setState(OrderState.loaded);
            });
        }
    });

    if (state === OrderState.loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className={classNames().orderDetails}>
            {renderOrderHeader(order)}
            <OrderSummary>
                <OrderSummaryInfo order={order} extra={renderOrderNotes.bind(null, order)}>
                    {renderDelivery(order)}
                    <br/>
                    {renderOrderCostDetails(order)}
                </OrderSummaryInfo>
                <OrderActions order={order}>
                    <DefaultButton text='Print Invocie' href={`/orderdetails/pdf/${order.id}`} />
                    {renderTracking(order)}
                    {renderReviewLink(order)}
                </OrderActions>
            </OrderSummary>
            <div className={classNames().addresses}>
                {renderAddressBlock('Shipping address')(order.shippingAddress)}
                {renderAddressBlock('Billing address')(order.billingAddress)}
            </div>
            {renderItems(order.items)}
        </div>);
};
